import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, Palette, Upload, Sparkles, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Branding() {
    const { session } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const fileRef = useRef(null);

    const [features, setFeatures] = useState(null);
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [currency, setCurrency] = useState('EUR');
    const [form, setForm] = useState({
        branding_primary_color: '#C8553D',
        branding_secondary_color: '#2D5F3F',
        branding_font_family: 'Helvetica',
        branding_org_name: '',
        branding_logo_url: '',
    });

    const reload = async () => {
        if (!currentWorkspace || !session) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/billing/status`,
                { params: { workspace_id: currentWorkspace.id },
                  headers: { Authorization: `Bearer ${session.access_token}` } });
            setFeatures(r.data.features);
            setSubs(r.data.subscriptions || []);
            const f = r.data.features || {};
            setForm({
                branding_primary_color: f.branding_primary_color || '#C8553D',
                branding_secondary_color: f.branding_secondary_color || '#2D5F3F',
                branding_font_family: f.branding_font_family || 'Helvetica',
                branding_org_name: f.branding_org_name || '',
                branding_logo_url: f.branding_logo_url || '',
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
            setFeatures({ white_label_active: false });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [currentWorkspace, session]);

    const purchase = async () => {
        if (!currentWorkspace?.is_admin) { toast.error('Admin uniquement'); return; }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/billing/checkout/white-label`,
                { workspace_id: currentWorkspace.id, currency },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            toast.success(`White-Label activé — ${r.data.amount || 50} ${r.data.currency || 'EUR'} (mock paiement)`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const uploadLogo = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\/(png|jpe?g|svg\+xml|webp)$/i.test(f.type)) { toast.error('Image PNG/JPG/SVG/WEBP requise'); return; }
        if (f.size > 2 * 1024 * 1024) { toast.error('Logo > 2 MB'); return; }
        setBusy(true);
        const path = `${currentWorkspace.id}/_branding/logo_${Date.now()}_${f.name.replace(/\s+/g, '_')}`;
        const { error: uErr } = await supabase.storage.from('documents').upload(path, f, { upsert: true, cacheControl: '3600' });
        if (uErr) { toast.error(uErr.message); setBusy(false); return; }
        const { data: signed } = await supabase.storage.from('documents').createSignedUrl(path, 60 * 60 * 24 * 365);
        const logo_url = signed?.signedUrl;
        setForm(prev => ({ ...prev, branding_logo_url: logo_url || prev.branding_logo_url }));
        toast.success('Logo téléversé — pensez à enregistrer');
        if (fileRef.current) fileRef.current.value = '';
        setBusy(false);
    };

    const save = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/branding/update`,
                { workspace_id: currentWorkspace.id, ...form },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            toast.success('Branding enregistré — tous les futurs exports en bénéficieront.');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">Aucun espace sélectionné</div>;
    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;

    const active = features?.white_label_active;
    const isAdmin = currentWorkspace.is_admin;

    return (
        <div className="space-y-8" data-testid="branding-page">
            <div>
                <div className="eyebrow mb-2">Personnalisation premium</div>
                <h1 className="font-heading text-4xl">White-Label sur vos exports</h1>
                <p className="text-foreground/70 mt-3 max-w-3xl">
                    Appliquez automatiquement votre logo et votre charte graphique sur tous les livrables PDF, Excel
                    et PowerPoint générés pour vos bailleurs et votre COMEX.
                </p>
            </div>

            {!active && (
                <div className="card-soft p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/30" data-testid="white-label-locked">
                    <div className="flex items-start gap-5">
                        <div className="h-14 w-14 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                            <Lock size={26} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-heading text-2xl">Activer le White-Label — 100 €</h2>
                            <p className="text-sm text-foreground/70 mt-2 leading-relaxed">
                                Paiement unique. Tous vos exports affichent votre logo, vos couleurs et le nom de votre
                                organisation. Idéal pour les bailleurs qui veulent présenter leurs rapports à leur image.
                            </p>
                            <ul className="text-sm text-foreground/70 mt-4 space-y-1.5">
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> Logo en en-tête des PDF</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> Couleur primaire + secondaire personnalisées</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> Nom d'organisation affiché à la place d'African Valley</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> Police custom (Helvetica, Times-Roman, Courier)</li>
                            </ul>
                            <div className="mt-6 flex items-center gap-3">
                                <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                                    className="h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="white-label-currency">
                                    <option value="EUR">EUR €</option>
                                    <option value="XOF">XOF (FCFA)</option>
                                    <option value="XAF">XAF (FCFA)</option>
                                </select>
                                <Button onClick={purchase} disabled={!isAdmin || busy}
                                    className="bg-primary hover:bg-primary-hover" data-testid="white-label-purchase-btn">
                                    {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-1.5" />}
                                    Activer pour 100 € (mock)
                                </Button>
                                {!isAdmin && <span className="text-xs text-muted-foreground">Réservé aux admins</span>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {active && (
                <>
                    <div className="card-soft p-5 bg-primary/5 border-primary/30 flex items-center gap-3" data-testid="white-label-active">
                        <CheckCircle2 className="text-primary" size={20} />
                        <div>
                            <div className="font-medium text-foreground">White-Label actif</div>
                            <div className="text-xs text-muted-foreground">Activé le {features.white_label_purchased_at ? new Date(features.white_label_purchased_at).toLocaleDateString() : '—'}</div>
                        </div>
                    </div>

                    <div className="card-soft p-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <Palette size={18} className="text-primary" />
                            <div className="font-heading text-xl">Charte graphique</div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium block mb-1.5">Nom de l'organisation (en-tête)</label>
                                <Input value={form.branding_org_name}
                                    onChange={(e) => setForm({ ...form, branding_org_name: e.target.value })}
                                    placeholder="ex. SENELEC · Pilotage Projet" data-testid="branding-org-name" />
                            </div>
                            <div>
                                <label className="text-sm font-medium block mb-1.5">Police</label>
                                <select value={form.branding_font_family}
                                    onChange={(e) => setForm({ ...form, branding_font_family: e.target.value })}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="branding-font">
                                    <option value="Helvetica">Helvetica (moderne)</option>
                                    <option value="Times-Roman">Times-Roman (institutionnel)</option>
                                    <option value="Courier">Courier (technique)</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium block mb-1.5">Couleur primaire</label>
                                <div className="flex gap-2">
                                    <input type="color" value={form.branding_primary_color}
                                        onChange={(e) => setForm({ ...form, branding_primary_color: e.target.value })}
                                        className="h-10 w-14 rounded-md border border-input cursor-pointer" data-testid="branding-primary-color" />
                                    <Input value={form.branding_primary_color}
                                        onChange={(e) => setForm({ ...form, branding_primary_color: e.target.value })}
                                        className="font-mono" />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium block mb-1.5">Couleur secondaire</label>
                                <div className="flex gap-2">
                                    <input type="color" value={form.branding_secondary_color}
                                        onChange={(e) => setForm({ ...form, branding_secondary_color: e.target.value })}
                                        className="h-10 w-14 rounded-md border border-input cursor-pointer" data-testid="branding-secondary-color" />
                                    <Input value={form.branding_secondary_color}
                                        onChange={(e) => setForm({ ...form, branding_secondary_color: e.target.value })}
                                        className="font-mono" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium block mb-1.5">Logo</label>
                            {form.branding_logo_url && (
                                <div className="mb-2 p-3 rounded-md bg-surface-muted inline-block">
                                    <img src={form.branding_logo_url} alt="logo" className="h-12 object-contain" />
                                </div>
                            )}
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="branding-logo-input" />
                            <div>
                                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="branding-logo-btn">
                                    <Upload size={14} className="mr-1.5" /> {form.branding_logo_url ? 'Remplacer le logo' : 'Téléverser un logo'}
                                </Button>
                                <span className="text-xs text-muted-foreground ml-3">PNG/JPG/SVG · 2 MB max</span>
                            </div>
                        </div>

                        <div className="pt-3 border-t border-border flex justify-end">
                            <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="branding-save-btn">
                                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Enregistrer le branding
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <div className="card-soft p-6">
                <div className="font-heading text-lg mb-2">Vos abonnements actifs</div>
                {subs.length === 0 ? (
                    <p className="text-sm text-foreground/60">Aucun pack souscrit. Découvrez la <a href="/app/marketplace" className="text-primary underline">marketplace</a>.</p>
                ) : (
                    <ul className="space-y-2">
                        {subs.map(s => (
                            <li key={s.id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0" data-testid={`sub-${s.template_packs?.slug}`}>
                                <span><strong>{s.template_packs?.name}</strong> — {s.template_packs?.sector}</span>
                                <span className="text-xs text-muted-foreground">jusqu'au {new Date(s.current_period_end).toLocaleDateString()}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
